// src/contexts/AuthContext.tsx

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthService } from '../services/api/authService';
import {
  LoginCredentials,
  AuthTokens,
  AuthError,
} from '../types/auth';
import { awsConfig } from '../services/config/aws-config';

// --- Extended Cognito-Specific Interfaces ---

/**
 * Represents standard and optional Cognito attributes.
 * Add or remove fields based on your Cognito user pool config.
 */
interface CognitoUserAttributes {
  sub: string;                // Cognito User Sub
  email: string;
  email_verified: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  phone_number?: string;
  phone_number_verified?: boolean;
  custom_attributes?: Record<string, string>; 
  // Add any other Cognito attributes you need
}

/**
 * Our main UserData interface extends Cognito attributes.
 * You can include extra fields like userGroups or lastLoginDate.
 */
export interface UserData extends CognitoUserAttributes {
  username: string;
  userGroups?: string[];
  lastLoginDate?: string;
}

// --- AuthContext Type with Additional Functions ---

interface AuthContextType {
  isAuthenticated: boolean;
  tokens: AuthTokens | null;
  user: UserData | null;
  loading: boolean;
  error: AuthError | null;
  signIn: (credentials: LoginCredentials) => Promise<void>;
  signOut: () => Promise<void>;
  refreshToken: () => Promise<void>;
  updateUser: (userData: Partial<UserData>) => void;
  clearError: () => void;

  // New helper methods:
  getUserAttribute: (attributeName: keyof UserData) => any;
  isUserInGroup: (groupName: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_REFRESH_INTERVAL = 1000 * 60 * 30; // 30 minutes

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);

  // Clears any stored error in state
  const clearError = () => setError(null);

  // Helper to start or reset the refresh interval for tokens
  const startRefreshInterval = useCallback(() => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }
    const interval = setInterval(() => {
      refreshToken();
    }, TOKEN_REFRESH_INTERVAL);
    setRefreshInterval(interval);
    return interval;
  }, [refreshInterval]);

  // Clear interval on unmount
  useEffect(() => {
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [refreshInterval]);

  // Attempt to load any stored auth data on initial mount
  useEffect(() => {
    loadStoredAuth();
  }, []);

  // Load tokens and user data from localStorage, validate or refresh token
  const loadStoredAuth = async () => {
    try {
      // If AWS config is invalid in production, skip auth check
      if (!awsConfig.isConfigValid) {
        setLoading(false);
        return;
      }
      
      const storedTokens = localStorage.getItem('auth_tokens');
      const storedUser = localStorage.getItem('auth_user');

      // Check for valid, non-"undefined" JSON strings
      if (
        storedTokens &&
        storedUser &&
        storedTokens !== 'undefined' &&
        storedUser !== 'undefined'
      ) {
        const parsedTokens: AuthTokens = JSON.parse(storedTokens);
        const parsedUser: UserData = JSON.parse(storedUser);

        if (AuthService.isTokenValid(parsedTokens.idToken)) {
          setTokens(parsedTokens);
          setUser(parsedUser);
          startRefreshInterval();
        } else {
          await refreshToken();
        }
      }
    } catch (error) {
      clearAuthData();
    } finally {
      setLoading(false);
    }
  };

  // Refresh the token using AuthService
  const refreshToken = async () => {
    try {
      if (tokens?.refreshToken) {
        const newTokens = await AuthService.refreshTokens(tokens.refreshToken);
        if (!newTokens) {
          throw new Error("Refresh token did not return new tokens.");
        }
        setTokens(newTokens);
        localStorage.setItem('auth_tokens', JSON.stringify(newTokens));
        startRefreshInterval();
      }
    } catch (error: any) {
      clearAuthData();
    }
  };

  // Clear everything in local state and localStorage
  const clearAuthData = () => {
    setTokens(null);
    setUser(null);
    setError(null);
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('auth_user');
    if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  };

  // Modified signIn to capture Cognito user attributes
  const signIn = async (credentials: LoginCredentials) => {
    try {
      setLoading(true);
      clearError();

      const response = await AuthService.signIn(credentials);
      const { tokens: newTokens, user: cognitoUser } = response || {};

      if (!newTokens || !cognitoUser) {
        throw new Error("Invalid sign-in response from AuthService");
      }

      // Extract user attributes from the Cognito response
      const userData: UserData = {
        username: cognitoUser.username,
        sub: cognitoUser.sub,
        email: cognitoUser.email,
        email_verified: cognitoUser.email_verified,
        name: cognitoUser.name,
        given_name: cognitoUser.given_name,
        family_name: cognitoUser.family_name,
        phone_number: cognitoUser.phone_number,
        phone_number_verified: cognitoUser.phone_number_verified,
        userGroups: cognitoUser.signInUserSession?.accessToken?.payload['cognito:groups'],
        lastLoginDate: new Date().toISOString(),
        custom_attributes: Object.keys(cognitoUser.attributes || {})
          .filter(key => key.startsWith('custom:'))
          .reduce((acc, key) => ({
            ...acc,
            [key.replace('custom:', '')]: cognitoUser.attributes[key]
          }), {})
      };

      setTokens(newTokens);
      setUser(userData);

      localStorage.setItem('auth_tokens', JSON.stringify(newTokens));
      localStorage.setItem('auth_user', JSON.stringify(userData));

      startRefreshInterval();
    } catch (error: any) {
      clearAuthData();
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign out using AuthService, then clear auth data
  const signOut = async () => {
    try {
      setLoading(true);
      clearError();
      
      if (tokens?.accessToken) {
        await AuthService.signOut(tokens.accessToken);
      }
      
      // Clear auth data
      clearAuthData();
    } catch (error) {
      clearAuthData();
    } finally {
      setLoading(false);
    }
  };

  // Update user data in local state & localStorage
  const updateUser = (userData: Partial<UserData>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
    }
  };

  // Get a specific user attribute, or null if not set
  const getUserAttribute = (attributeName: keyof UserData) => {
    return user ? user[attributeName] : null;
  };

  // Check if the current user is in a particular Cognito group
  const isUserInGroup = (groupName: string) => {
    return user?.userGroups?.includes(groupName) || false;
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!tokens && !!user,
        tokens,
        user,
        loading,
        error,
        signIn,
        signOut,
        refreshToken,
        updateUser,
        clearError,
        getUserAttribute,
        isUserInGroup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
